import {noop} from '@benchling/js-utils';
import {css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ACTION_CATEGORY, ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {Section} from 'constants/NavConstants';
import {getTabStyles, navBarIconStyles} from 'nav/navbar/NavBarStyles';
import {sectionToReadable} from 'nav/NavUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {snakeToCamelCase} from 'util/NameUtil';
import {ElementMouseEvent} from 'util/Types';

export interface NavBarIconProps {
  className?: string;
  section: Section;
  icon: BenchlingIconName;
  isActive: boolean;
  link?: string;
  onSectionClick?: (section: Section, e?: ElementMouseEvent) => void;
  hasActivity: boolean;
}

class NavBarIcon extends React.Component<NavBarIconProps> {
  static defaultProps = {
    hasActivity: false,
    isActive: false,
  };

  _handleSectionClick = (e: ElementMouseEvent): void => {
    if (this.props.link && this.props.onSectionClick) {
      // If both a link and click handler are provided, we want the click handler behavior to override
      // the default link action
      e.preventDefault();
    }
    trackEvent(
      {
        module: ANALYTICS_MODULE.NAVIGATION,
        actionCategory: ACTION_CATEGORY.READ,
        action: 'clicked_navbar_icon',
      },
      {section: this.props.section}
    );
    if (this.props.onSectionClick) {
      this.props.onSectionClick(this.props.section, e);
    }
  };

  override render(): JSX.Element {
    const showActivityIndicator = this.props.hasActivity && !this.props.isActive;
    const innerIcon = (
      <div className={css(navBarIconStyles.inner)}>
        <BenchlingIcon
          className={classNames(css(navBarIconStyles.icon), this.props.className)}
          icon={this.props.icon}
          aria-hidden="true"
        />
        {showActivityIndicator && (
          <div
            className={classNames(
              css(navBarIconStyles.activityIndicator),
              '_test-navBarIcon-activityIndicator'
            )}
            {...getTestProps({component: 'NavBarIcon', element: 'activityIndicator'})}
          />
        )}
      </div>
    );
    const dataTestAttrs = {
      component: 'NavBarIcon',
      key: snakeToCamelCase(this.props.section.toLowerCase()),
    };
    return (
      <Tooltip
        isDisabled={this.props.isActive}
        placement={FloatingPlacement.RIGHT}
        text={sectionToReadable(this.props.section)}
      >
        <div
          id={`${this.props.section}-tab`}
          aria-controls={`${this.props.section}-panel`}
          aria-label={sectionToReadable(this.props.section) ?? ''}
          aria-selected={this.props.isActive}
          role="tab"
          onClick={this.props.link ? noop : this._handleSectionClick}
          tabIndex={0}
          className={css(getTabStyles(this.props.isActive))}
        >
          {this.props.link ? (
            <a
              href={this.props.link}
              onClick={this._handleSectionClick}
              className={css(navBarIconStyles.iconWrapper)}
              {...getTestProps(dataTestAttrs)}
            >
              {innerIcon}
            </a>
          ) : (
            <div className={css(navBarIconStyles.iconWrapper)} {...getTestProps(dataTestAttrs)}>
              {innerIcon}
            </div>
          )}
        </div>
      </Tooltip>
    );
  }
}

export default NavBarIcon;
